import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, Users, DollarSign, Settings, CheckCircle, XCircle, Search, Loader, Calendar, Download, FileText, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('applications');
  const [loading, setLoading] = useState(false);
  
  // Coach Applications State
  const [coachApplications, setCoachApplications] = useState([]);
  const [applicationFilter, setApplicationFilter] = useState('pending');
  
  // Students State
  const [students, setStudents] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');
  
  // Revenue State
  const [revenue, setRevenue] = useState(null);
  const [revenueMonth, setRevenueMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [gstFilingNotes, setGstFilingNotes] = useState({});
  
  // Settings State
  const [platformFee, setPlatformFee] = useState(10);
  const [gstRate, setGstRate] = useState(18);
  const [razorpayFee, setRazorpayFee] = useState(2.5);
  const [settingsLoading, setSettingsLoading] = useState(false);
  
  useEffect(() => {
    if (activeTab === 'applications') {
      fetchCoachApplications();
    } else if (activeTab === 'students') {
      fetchStudents();
    } else if (activeTab === 'revenue') {
      fetchRevenue();
    } else if (activeTab === 'settings') {
      fetchSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, applicationFilter]);
  
  const fetchCoachApplications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/coaches/pending`, { 
        withCredentials: true 
      });
      setCoachApplications(response.data);
    } catch (error) {
      console.error('Error fetching coach applications:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      } else {
        toast.error('Failed to load coach applications');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/students`, { 
        withCredentials: true,
        params: { search: studentSearch }
      });
      setStudents(response.data.students || []);
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchRevenue = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/revenue`, { 
        withCredentials: true 
      });
      setRevenue(response.data);
    } catch (error) {
      console.error('Error fetching revenue:', error);
      toast.error('Failed to load revenue data');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/settings`, { 
        withCredentials: true 
      });
      setPlatformFee(response.data.platform_fee_percentage || 10);
      setGstRate(response.data.gst_rate || 18);
      setRazorpayFee(response.data.razorpay_fee || 2.5);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };
  
  const handleApproveCoach = async (coachId) => {
    try {
      await axios.put(
        `${API}/admin/coaches/${coachId}/verify`,
        { status: 'verified' },
        { withCredentials: true }
      );
      toast.success('Coach approved successfully');
      fetchCoachApplications();
    } catch (error) {
      console.error('Error approving coach:', error);
      toast.error('Failed to approve coach');
    }
  };
  
  const handleRejectCoach = async (coachId) => {
    try {
      await axios.put(
        `${API}/admin/coaches/${coachId}/verify`,
        { status: 'rejected' },
        { withCredentials: true }
      );
      toast.success('Coach rejected');
      fetchCoachApplications();
    } catch (error) {
      console.error('Error rejecting coach:', error);
      toast.error('Failed to reject coach');
    }
  };
  
  const handleUpdatePlatformFee = async () => {
    setSettingsLoading(true);
    try {
      await axios.put(
        `${API}/admin/settings`,
        { 
          platform_fee_percentage: platformFee,
          gst_rate: gstRate,
          razorpay_fee: razorpayFee
        },
        { withCredentials: true }
      );
      toast.success('Platform settings updated successfully');
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setSettingsLoading(false);
    }
  };
  
  // Helper functions for Revenue tab
  const getGSTFilingStatus = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    const daysUntil = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntil < 0) return { status: 'Overdue', color: 'red' };
    if (daysUntil <= 7) return { status: 'Due Soon', color: 'gold' };
    return { status: 'Upcoming', color: 'white' };
  };
  
  const getMonthlyGSTTotal = (month) => {
    // Calculate GST collected for a specific month
    // This would filter transactions by month and sum up GST
    if (!revenue || !revenue.gst_collected) return 0;
    return revenue.gst_collected;
  };
  
  const exportToCSV = () => {
    // Export revenue data to CSV
    if (!revenue || !revenue.course_breakdown) {
      toast.error('No data to export');
      return;
    }
    
    const headers = ['Date', 'Student', 'Coach', 'Course', 'Total Paid', 'Platform Fee', 'GST', 'Razorpay Fee', 'DIOS Net'];
    const rows = revenue.course_breakdown.map(course => [
      new Date().toLocaleDateString(),
      '-',
      '-',
      course.course_title,
      course.total,
      (course.total * 0.10).toFixed(2), // Platform fee 10%
      (course.total * 0.10 * 0.18).toFixed(2), // GST 18% of platform fee
      (course.total * 0.025).toFixed(2), // Razorpay 2.5%
      ((course.total * 0.10) - (course.total * 0.025)).toFixed(2) // DIOS net
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `revenue_${revenueMonth}.csv`;
    a.click();
    toast.success('Revenue data exported');
  };
  
  const tabs = [
    { id: 'applications', label: 'Coach Applications', icon: Users },
    { id: 'students', label: 'Students', icon: Users },
    { id: 'revenue', label: 'Revenue', icon: DollarSign },
    { id: 'settings', label: 'Platform Settings', icon: Settings },
  ];
  
  return (
    <div className="min-h-screen bg-deep-dark py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <span className="inline-block px-4 py-1 bg-premium-gold/20 text-premium-gold rounded-full text-sm font-semibold tracking-wider mb-4">
            <Shield className="w-4 h-4 inline mr-1" strokeWidth={1.5} /> Admin Portal
          </span>
          <h1 
            className="text-4xl md:text-5xl font-bold tracking-tight text-white"
            style={{ fontFamily: 'Fraunces, serif' }}
          >
            Admin <span className="gold-gradient">Dashboard</span>
          </h1>
        </div>
        
        {/* Tabs */}
        <div className="border-b border-premium-gold/30 mb-8">
          <div className="flex gap-2 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-3 font-semibold transition-all whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'text-premium-gold border-b-2 border-premium-gold'
                      : 'text-white/60 hover:text-white/80'
                  }`}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Tab Content */}
        <div className="mt-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader className="w-8 h-8 text-premium-gold animate-spin" />
            </div>
          ) : (
            <>
              {/* Coach Applications Tab */}
              {activeTab === 'applications' && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Fraunces, serif' }}>
                      Coach Applications
                    </h2>
                    <p className="text-white/60">Review and approve coach applications</p>
                  </div>
                  
                  {coachApplications.length === 0 ? (
                    <div className="bg-dark-surface p-12 rounded-xl border border-premium-gold/30 text-center">
                      <Users className="w-16 h-16 text-premium-gold/50 mx-auto mb-4" strokeWidth={1.5} />
                      <p className="text-white/60 text-lg">No pending coach applications</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {coachApplications.map((coach) => (
                        <div 
                          key={coach.user_id} 
                          className="bg-dark-surface p-6 rounded-xl border border-premium-gold/30"
                        >
                          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                            {/* Coach Info */}
                            <div className="flex gap-4 flex-1">
                              {coach.picture ? (
                                <img 
                                  src={coach.picture} 
                                  alt={coach.name} 
                                  className="w-16 h-16 rounded-full border-2 border-premium-gold/30" 
                                />
                              ) : (
                                <div className="w-16 h-16 rounded-full bg-premium-gold/20 border-2 border-premium-gold/30 flex items-center justify-center">
                                  <Users className="w-8 h-8 text-premium-gold" strokeWidth={1.5} />
                                </div>
                              )}
                              
                              <div className="flex-1">
                                <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Fraunces, serif' }}>
                                  {coach.name}
                                </h3>
                                <p className="text-white/50 text-sm mb-3">{coach.email}</p>
                                <p className="text-white/70 mb-4">{coach.bio}</p>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <span className="font-semibold text-white/80">Sports:</span>
                                    <p className="text-white/60">{coach.specializations?.join(', ')}</p>
                                  </div>
                                  <div>
                                    <span className="font-semibold text-white/80">Experience:</span>
                                    <p className="text-white/60">{coach.experience_years} years</p>
                                  </div>
                                  <div>
                                    <span className="font-semibold text-white/80">Rate:</span>
                                    <p className="text-premium-gold font-semibold">₹{coach.hourly_rate}/hour</p>
                                  </div>
                                  {coach.certifications && coach.certifications.length > 0 && (
                                    <div>
                                      <span className="font-semibold text-white/80">Certifications:</span>
                                      <p className="text-white/60">{coach.certifications.join(', ')}</p>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Bank Details (Admin Only) */}
                                {(coach.bank_account_number || coach.upi_id) && (
                                  <div className="mt-4 p-4 bg-deep-dark rounded-lg border border-premium-gold/20">
                                    <p className="text-xs font-semibold text-premium-gold mb-2">Bank Details (Private)</p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                                      {coach.bank_account_holder_name && (
                                        <div>
                                          <span className="text-white/60">Account Holder:</span>
                                          <p className="text-white font-mono">{coach.bank_account_holder_name}</p>
                                        </div>
                                      )}
                                      {coach.bank_account_number && (
                                        <div>
                                          <span className="text-white/60">Account Number:</span>
                                          <p className="text-white font-mono">{coach.bank_account_number}</p>
                                        </div>
                                      )}
                                      {coach.bank_ifsc && (
                                        <div>
                                          <span className="text-white/60">IFSC Code:</span>
                                          <p className="text-white font-mono">{coach.bank_ifsc}</p>
                                        </div>
                                      )}
                                      {coach.upi_id && (
                                        <div>
                                          <span className="text-white/60">UPI ID:</span>
                                          <p className="text-white font-mono">{coach.upi_id}</p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex lg:flex-col gap-3">
                              <button
                                onClick={() => handleApproveCoach(coach.user_id)}
                                className="flex-1 lg:flex-initial flex items-center justify-center gap-2 px-6 py-3 border-2 border-premium-gold text-premium-gold hover:bg-premium-gold hover:text-white font-semibold rounded-lg transition-all"
                              >
                                <CheckCircle className="w-5 h-5" strokeWidth={1.5} />
                                Approve
                              </button>
                              <button
                                onClick={() => handleRejectCoach(coach.user_id)}
                                className="flex-1 lg:flex-initial flex items-center justify-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-all"
                              >
                                <XCircle className="w-5 h-5" strokeWidth={1.5} />
                                Reject
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Students Tab */}
              {activeTab === 'students' && (
                <div>
                  <div className="mb-6 flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Fraunces, serif' }}>
                        Students
                      </h2>
                      <p className="text-white/60">All registered students on the platform</p>
                    </div>
                    <div className="relative">
                      <Search className="w-5 h-5 text-white/40 absolute left-3 top-1/2 transform -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Search students..."
                        value={studentSearch}
                        onChange={(e) => setStudentSearch(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && fetchStudents()}
                        className="pl-10 pr-4 py-2 bg-dark-surface border border-premium-gold/30 rounded-lg text-white placeholder-white/40 focus:border-premium-gold focus:ring-2 focus:ring-premium-gold/30 focus:outline-none"
                      />
                    </div>
                  </div>
                  
                  {students.length === 0 ? (
                    <div className="bg-dark-surface p-12 rounded-xl border border-premium-gold/30 text-center">
                      <Users className="w-16 h-16 text-premium-gold/50 mx-auto mb-4" strokeWidth={1.5} />
                      <p className="text-white/60 text-lg">No students found</p>
                    </div>
                  ) : (
                    <div className="bg-dark-surface rounded-xl border border-premium-gold/30 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-deep-dark border-b border-premium-gold/30">
                            <tr>
                              <th className="px-6 py-4 text-left text-sm font-semibold text-white/80">Student</th>
                              <th className="px-6 py-4 text-left text-sm font-semibold text-white/80">Email</th>
                              <th className="px-6 py-4 text-left text-sm font-semibold text-white/80">Courses Enrolled</th>
                              <th className="px-6 py-4 text-left text-sm font-semibold text-white/80">Total Paid</th>
                              <th className="px-6 py-4 text-left text-sm font-semibold text-white/80">Joined</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-premium-gold/20">
                            {students.map((student) => (
                              <tr key={student.user_id} className="hover:bg-premium-gold/5 transition-colors">
                                <td className="px-6 py-4">
                                  <div className="flex items-center gap-3">
                                    {student.picture ? (
                                      <img 
                                        src={student.picture} 
                                        alt={student.name} 
                                        className="w-10 h-10 rounded-full border border-premium-gold/30" 
                                      />
                                    ) : (
                                      <div className="w-10 h-10 rounded-full bg-premium-gold/20 border border-premium-gold/30 flex items-center justify-center">
                                        <Users className="w-5 h-5 text-premium-gold" strokeWidth={1.5} />
                                      </div>
                                    )}
                                    <span className="font-semibold text-white">{student.name}</span>
                                  </div>
                                </td>
                                <td className="px-6 py-4 text-white/60">{student.email}</td>
                                <td className="px-6 py-4 text-white">{student.courses_enrolled || 0}</td>
                                <td className="px-6 py-4 text-premium-gold font-semibold">
                                  ₹{student.total_paid?.toFixed(0) || 0}
                                </td>
                                <td className="px-6 py-4 text-white/60">
                                  {student.created_at ? new Date(student.created_at).toLocaleDateString() : 'N/A'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Revenue Tab */}
              {activeTab === 'revenue' && (
                <div>
                  {/* Header with Month Filter */}
                  <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Fraunces, serif' }}>
                        Revenue & GST Compliance
                      </h2>
                      <p className="text-white/60">Platform revenue, payouts, and GST filing schedule</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="month"
                        value={revenueMonth}
                        onChange={(e) => setRevenueMonth(e.target.value)}
                        className="px-4 py-2 bg-dark-surface border border-premium-gold/30 rounded-lg text-white focus:border-premium-gold focus:ring-2 focus:ring-premium-gold/30 focus:outline-none"
                      />
                      <button
                        onClick={exportToCSV}
                        className="flex items-center gap-2 px-4 py-2 border-2 border-premium-gold text-premium-gold hover:bg-premium-gold hover:text-white font-semibold rounded-lg transition-all"
                      >
                        <Download className="w-4 h-4" strokeWidth={1.5} />
                        Export CSV
                      </button>
                    </div>
                  </div>
                  
                  {revenue ? (
                    <>
                      {/* 5 Revenue Summary Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
                        <div className="bg-dark-surface p-5 rounded-xl border border-premium-gold/30">
                          <p className="text-xs text-white/60 font-semibold mb-1">Total Collected</p>
                          <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                            ₹{revenue.total_revenue?.toFixed(0) || 0}
                          </p>
                          <p className="text-xs text-white/40 mt-1">
                            {revenue.transaction_count || 0} payments
                          </p>
                        </div>
                        
                        <div className="bg-dark-surface p-5 rounded-xl border border-premium-gold/30">
                          <p className="text-xs text-white/60 font-semibold mb-1">Coach Payouts</p>
                          <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                            ₹{revenue.coach_payouts?.toFixed(0) || 0}
                          </p>
                          <p className="text-xs text-white/40 mt-1">Full set prices</p>
                        </div>
                        
                        {/* GST Card - Special Gold Styling */}
                        <div className="bg-dark-surface p-5 rounded-xl border-2 border-premium-gold/50 relative">
                          <div className="absolute top-2 right-2">
                            <FileText className="w-4 h-4 text-premium-gold" strokeWidth={1.5} />
                          </div>
                          <p className="text-xs text-premium-gold font-semibold mb-1">GST Collected</p>
                          <p className="text-2xl font-bold text-premium-gold" style={{ fontFamily: 'Fraunces, serif' }}>
                            ₹{revenue.gst_collected?.toFixed(0) || 0}
                          </p>
                          <p className="text-xs text-premium-gold/60 mt-1 font-semibold">
                            Remit via Formatking Originals GSTIN
                          </p>
                        </div>
                        
                        <div className="bg-dark-surface p-5 rounded-xl border border-premium-gold/30">
                          <p className="text-xs text-white/60 font-semibold mb-1">Razorpay Fees</p>
                          <p className="text-2xl font-bold text-red-400" style={{ fontFamily: 'Fraunces, serif' }}>
                            ₹{revenue.razorpay_fees?.toFixed(0) || 0}
                          </p>
                          <p className="text-xs text-white/40 mt-1">2.5% of total</p>
                        </div>
                        
                        <div className="bg-dark-surface p-5 rounded-xl border border-premium-gold/30">
                          <p className="text-xs text-white/60 font-semibold mb-1">DIOS Net Earnings</p>
                          <p className="text-2xl font-bold text-premium-gold" style={{ fontFamily: 'Fraunces, serif' }}>
                            ₹{revenue.dios_net?.toFixed(0) || 0}
                          </p>
                          <p className="text-xs text-white/40 mt-1">After Razorpay</p>
                        </div>
                      </div>
                      
                      {/* GST Filing Calendar Section */}
                      <div className="bg-dark-surface p-6 rounded-xl border border-premium-gold/30 mb-8">
                        <div className="flex items-center gap-3 mb-6">
                          <Calendar className="w-6 h-6 text-premium-gold" strokeWidth={1.5} />
                          <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                            GST Filing Calendar
                          </h3>
                        </div>
                        
                        {/* Current and Previous Month GST */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                          <div className="bg-deep-dark p-4 rounded-lg border border-premium-gold/20">
                            <p className="text-sm text-white/60 mb-2">Current Month GST</p>
                            <p className="text-2xl font-bold text-premium-gold" style={{ fontFamily: 'Fraunces, serif' }}>
                              ₹{getMonthlyGSTTotal(new Date().toISOString().slice(0, 7))}
                            </p>
                            <p className="text-xs text-white/40 mt-1">
                              {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} (Running total)
                            </p>
                          </div>
                          
                          <div className="bg-deep-dark p-4 rounded-lg border border-premium-gold/20">
                            <p className="text-sm text-white/60 mb-2">Previous Month GST</p>
                            <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                              ₹{getMonthlyGSTTotal(new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().slice(0, 7))}
                            </p>
                            <p className="text-xs text-white/40 mt-1">
                              {new Date(new Date().setMonth(new Date().getMonth() - 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} (Needs filing)
                            </p>
                          </div>
                        </div>
                        
                        {/* Filing Deadlines Table */}
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-premium-gold/30">
                                <th className="px-4 py-3 text-left text-sm font-semibold text-white/80">Filing Type</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-white/80">Due Date</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-white/80">Amount</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-white/80">Status</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-white/80">Notes</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-premium-gold/20">
                              {/* GSTR-1 - Sales Return - 11th of every month */}
                              {(() => {
                                const today = new Date();
                                const currentMonth = today.getMonth();
                                const currentYear = today.getFullYear();
                                const dueDate = new Date(currentYear, currentMonth, 11);
                                if (today.getDate() > 11) {
                                  dueDate.setMonth(currentMonth + 1);
                                }
                                const { status, color } = getGSTFilingStatus(dueDate);
                                const daysRemaining = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
                                
                                return (
                                  <tr className="hover:bg-premium-gold/5 transition-colors">
                                    <td className="px-4 py-4">
                                      <div className="flex items-center gap-2">
                                        <FileText className="w-4 h-4 text-premium-gold" strokeWidth={1.5} />
                                        <div>
                                          <p className="text-white font-semibold">GSTR-1</p>
                                          <p className="text-xs text-white/50">Sales return</p>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-4 py-4">
                                      <p className="text-white">{dueDate.toLocaleDateString('en-IN')}</p>
                                      <p className="text-xs text-white/50">
                                        {daysRemaining > 0 ? `${daysRemaining} days remaining` : `${Math.abs(daysRemaining)} days overdue`}
                                      </p>
                                    </td>
                                    <td className="px-4 py-4 text-premium-gold font-semibold">
                                      ₹{revenue.gst_collected?.toFixed(0) || 0}
                                    </td>
                                    <td className="px-4 py-4">
                                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                        color === 'red' ? 'bg-red-600 text-white' :
                                        color === 'gold' ? 'bg-premium-gold/20 text-premium-gold border border-premium-gold' :
                                        'bg-white/10 text-white'
                                      }`}>
                                        {status}
                                      </span>
                                    </td>
                                    <td className="px-4 py-4">
                                      <input
                                        type="text"
                                        placeholder="Add reference..."
                                        value={gstFilingNotes['gstr1'] || ''}
                                        onChange={(e) => setGstFilingNotes({...gstFilingNotes, gstr1: e.target.value})}
                                        className="px-3 py-1 bg-deep-dark border border-premium-gold/20 rounded text-sm text-white placeholder-white/30 focus:border-premium-gold focus:outline-none w-full"
                                      />
                                    </td>
                                  </tr>
                                );
                              })()}
                              
                              {/* GSTR-3B - Summary + Payment - 20th of every month */}
                              {(() => {
                                const today = new Date();
                                const currentMonth = today.getMonth();
                                const currentYear = today.getFullYear();
                                const dueDate = new Date(currentYear, currentMonth, 20);
                                if (today.getDate() > 20) {
                                  dueDate.setMonth(currentMonth + 1);
                                }
                                const { status, color } = getGSTFilingStatus(dueDate);
                                const daysRemaining = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
                                
                                return (
                                  <tr className="hover:bg-premium-gold/5 transition-colors">
                                    <td className="px-4 py-4">
                                      <div className="flex items-center gap-2">
                                        <FileText className="w-4 h-4 text-premium-gold" strokeWidth={1.5} />
                                        <div>
                                          <p className="text-white font-semibold">GSTR-3B</p>
                                          <p className="text-xs text-white/50">Summary + payment</p>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-4 py-4">
                                      <p className="text-white">{dueDate.toLocaleDateString('en-IN')}</p>
                                      <p className="text-xs text-white/50">
                                        {daysRemaining > 0 ? `${daysRemaining} days remaining` : `${Math.abs(daysRemaining)} days overdue`}
                                      </p>
                                    </td>
                                    <td className="px-4 py-4 text-premium-gold font-semibold">
                                      ₹{revenue.gst_collected?.toFixed(0) || 0}
                                    </td>
                                    <td className="px-4 py-4">
                                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                        color === 'red' ? 'bg-red-600 text-white' :
                                        color === 'gold' ? 'bg-premium-gold/20 text-premium-gold border border-premium-gold' :
                                        'bg-white/10 text-white'
                                      }`}>
                                        {status}
                                      </span>
                                    </td>
                                    <td className="px-4 py-4">
                                      <input
                                        type="text"
                                        placeholder="Add reference..."
                                        value={gstFilingNotes['gstr3b'] || ''}
                                        onChange={(e) => setGstFilingNotes({...gstFilingNotes, gstr3b: e.target.value})}
                                        className="px-3 py-1 bg-deep-dark border border-premium-gold/20 rounded text-sm text-white placeholder-white/30 focus:border-premium-gold focus:outline-none w-full"
                                      />
                                    </td>
                                  </tr>
                                );
                              })()}
                              
                              {/* GSTR-9 - Annual Return - 31st December */}
                              {(() => {
                                const today = new Date();
                                const currentYear = today.getFullYear();
                                const dueDate = new Date(currentYear, 11, 31); // December 31
                                const { status, color } = getGSTFilingStatus(dueDate);
                                const monthsRemaining = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24 * 30));
                                
                                return (
                                  <tr className="hover:bg-premium-gold/5 transition-colors">
                                    <td className="px-4 py-4">
                                      <div className="flex items-center gap-2">
                                        <FileText className="w-4 h-4 text-premium-gold" strokeWidth={1.5} />
                                        <div>
                                          <p className="text-white font-semibold">GSTR-9</p>
                                          <p className="text-xs text-white/50">Annual return</p>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-4 py-4">
                                      <p className="text-white">{dueDate.toLocaleDateString('en-IN')}</p>
                                      <p className="text-xs text-white/50">
                                        {monthsRemaining > 0 ? `${monthsRemaining} months remaining` : 'Overdue'}
                                      </p>
                                    </td>
                                    <td className="px-4 py-4 text-premium-gold font-semibold">
                                      ₹{(revenue.gst_collected * 12)?.toFixed(0) || 0}
                                    </td>
                                    <td className="px-4 py-4">
                                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                        color === 'red' ? 'bg-red-600 text-white' :
                                        color === 'gold' ? 'bg-premium-gold/20 text-premium-gold border border-premium-gold' :
                                        'bg-white/10 text-white'
                                      }`}>
                                        {status}
                                      </span>
                                    </td>
                                    <td className="px-4 py-4">
                                      <input
                                        type="text"
                                        placeholder="Add reference..."
                                        value={gstFilingNotes['gstr9'] || ''}
                                        onChange={(e) => setGstFilingNotes({...gstFilingNotes, gstr9: e.target.value})}
                                        className="px-3 py-1 bg-deep-dark border border-premium-gold/20 rounded text-sm text-white placeholder-white/30 focus:border-premium-gold focus:outline-none w-full"
                                      />
                                    </td>
                                  </tr>
                                );
                              })()}
                            </tbody>
                          </table>
                        </div>
                        
                        <div className="mt-4 flex items-start gap-2 p-3 bg-premium-gold/10 rounded-lg border border-premium-gold/30">
                          <AlertCircle className="w-5 h-5 text-premium-gold flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                          <p className="text-sm text-premium-gold/90">
                            <span className="font-semibold">Important:</span> GST collected (₹{revenue.gst_collected?.toFixed(0) || 0}) must be remitted to the government via Formatking Originals GSTIN. This amount is a pass-through tax and not DIOS earnings.
                          </p>
                        </div>
                      </div>
                      
                      {/* Course Breakdown */}
                      {revenue.course_breakdown && revenue.course_breakdown.length > 0 && (
                        <div className="bg-dark-surface rounded-xl border border-premium-gold/30 overflow-hidden">
                          <div className="px-6 py-4 bg-deep-dark border-b border-premium-gold/30">
                            <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                              Revenue by Course
                            </h3>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead className="bg-deep-dark/50">
                                <tr>
                                  <th className="px-6 py-3 text-left text-sm font-semibold text-white/80">Course</th>
                                  <th className="px-6 py-3 text-left text-sm font-semibold text-white/80">Enrollments</th>
                                  <th className="px-6 py-3 text-left text-sm font-semibold text-white/80">Total Revenue</th>
                                  <th className="px-6 py-3 text-left text-sm font-semibold text-white/80">GST Collected</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-premium-gold/20">
                                {revenue.course_breakdown.map((course) => (
                                  <tr key={course.course_id} className="hover:bg-premium-gold/5 transition-colors">
                                    <td className="px-6 py-4 text-white">{course.course_title}</td>
                                    <td className="px-6 py-4 text-white/60">{course.count}</td>
                                    <td className="px-6 py-4 text-premium-gold font-semibold">
                                      ₹{course.total?.toFixed(0) || 0}
                                    </td>
                                    <td className="px-6 py-4 text-premium-gold/70">
                                      ₹{((course.total * 0.10) * 0.18)?.toFixed(0) || 0}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="bg-dark-surface p-12 rounded-xl border border-premium-gold/30 text-center">
                      <DollarSign className="w-16 h-16 text-premium-gold/50 mx-auto mb-4" strokeWidth={1.5} />
                      <p className="text-white/60 text-lg">No revenue data available</p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Settings Tab */}
              {activeTab === 'settings' && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Fraunces, serif' }}>
                      Platform Settings
                    </h2>
                    <p className="text-white/60">Manage platform configuration</p>
                  </div>
                  
                  <div className="bg-dark-surface p-8 rounded-xl border border-premium-gold/30 max-w-3xl">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                      <div>
                        <label className="block text-sm font-semibold text-white/80 mb-2">
                          Platform Fee %
                        </label>
                        <p className="text-xs text-white/50 mb-3">
                          Added on top of coach price
                        </p>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.5"
                            value={platformFee}
                            onChange={(e) => setPlatformFee(parseFloat(e.target.value))}
                            className="w-24 px-3 py-2 bg-deep-dark border border-premium-gold/30 rounded-lg text-white focus:border-premium-gold focus:ring-2 focus:ring-premium-gold/30 focus:outline-none"
                          />
                          <span className="text-white/60">%</span>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-white/80 mb-2">
                          GST Rate %
                        </label>
                        <p className="text-xs text-white/50 mb-3">
                          On platform fee only
                        </p>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.5"
                            value={gstRate}
                            onChange={(e) => setGstRate(parseFloat(e.target.value))}
                            className="w-24 px-3 py-2 bg-deep-dark border border-premium-gold/30 rounded-lg text-white focus:border-premium-gold focus:ring-2 focus:ring-premium-gold/30 focus:outline-none"
                          />
                          <span className="text-white/60">%</span>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-white/80 mb-2">
                          Razorpay Fee %
                        </label>
                        <p className="text-xs text-white/50 mb-3">
                          Deducted from settlement
                        </p>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            value={razorpayFee}
                            onChange={(e) => setRazorpayFee(parseFloat(e.target.value))}
                            className="w-24 px-3 py-2 bg-deep-dark border border-premium-gold/30 rounded-lg text-white focus:border-premium-gold focus:ring-2 focus:ring-premium-gold/30 focus:outline-none"
                          />
                          <span className="text-white/60">%</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-deep-dark p-6 rounded-lg mb-6 border border-premium-gold/20">
                      <p className="text-sm font-semibold text-premium-gold mb-4">DIOS Revenue Model — Fee Added ON TOP</p>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-white/50">Coach set price:</span>
                          <span className="text-white font-semibold">₹10,000</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/50">DIOS platform fee ({platformFee}%):</span>
                          <span className="text-white">₹{(10000 * (platformFee / 100)).toFixed(0)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/50">GST on platform fee ({gstRate}%):</span>
                          <span className="text-white">₹{((10000 * (platformFee / 100)) * (gstRate / 100)).toFixed(0)}</span>
                        </div>
                        <div className="flex justify-between border-t border-premium-gold/30 pt-2 mt-2">
                          <span className="text-white/70 font-semibold">Student total pays:</span>
                          <span className="text-premium-gold font-bold">₹{(10000 + (10000 * (platformFee / 100)) + ((10000 * (platformFee / 100)) * (gstRate / 100))).toFixed(0)}</span>
                        </div>
                        
                        <div className="h-px bg-premium-gold/20 my-3"></div>
                        
                        <div className="flex justify-between">
                          <span className="text-white/50">Razorpay fee ({razorpayFee}% of total):</span>
                          <span className="text-red-400">-₹{((10000 + (10000 * (platformFee / 100)) + ((10000 * (platformFee / 100)) * (gstRate / 100))) * (razorpayFee / 100)).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between border-t border-premium-gold/30 pt-2 mt-2">
                          <span className="text-white/70 font-semibold">Net settlement received:</span>
                          <span className="text-white font-bold">₹{((10000 + (10000 * (platformFee / 100)) + ((10000 * (platformFee / 100)) * (gstRate / 100))) - ((10000 + (10000 * (platformFee / 100)) + ((10000 * (platformFee / 100)) * (gstRate / 100))) * (razorpayFee / 100))).toFixed(2)}</span>
                        </div>
                        
                        <div className="h-px bg-premium-gold/20 my-3"></div>
                        
                        <div className="flex justify-between">
                          <span className="text-white/70">Coach payout:</span>
                          <span className="text-white">-₹10,000</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/70">GST held for govt:</span>
                          <span className="text-white">-₹{((10000 * (platformFee / 100)) * (gstRate / 100)).toFixed(0)}</span>
                        </div>
                        <div className="flex justify-between border-t border-premium-gold/30 pt-2 mt-2">
                          <span className="text-white/70 font-semibold">DIOS net earnings:</span>
                          <span className="text-premium-gold font-bold">₹{(
                            (10000 * (platformFee / 100)) - 
                            ((10000 + (10000 * (platformFee / 100)) + ((10000 * (platformFee / 100)) * (gstRate / 100))) * (razorpayFee / 100))
                          ).toFixed(2)}</span>
                        </div>
                      </div>
                      
                      <p className="text-xs text-white/40 mt-4 italic">
                        ✓ Coach always receives exactly what they set (₹10,000)
                        <br />
                        ✓ Platform fee & GST are additional charges to the student
                        <br />
                        ✓ GST is a pass-through tax remitted to govt (not DIOS earnings)
                      </p>
                    </div>
                    
                    <button
                      onClick={handleUpdatePlatformFee}
                      disabled={settingsLoading}
                      className="w-full bg-premium-gold hover:bg-premium-gold-dark text-white font-semibold px-6 py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {settingsLoading ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" />
                          Updating...
                        </>
                      ) : (
                        'Update Platform Settings'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
